import { useContext, useEffect, useMemo, useState } from 'react';

import {
  CommodityCurrency,
  CommodityDate,
  UserIdentity,
} from '@/components/RightProvider/const';
import { RightContext } from '@/components/RightProvider/context';
import { LimitConfig } from '@/components/RightProvider/type';
import ShineText from '@/components/UIComponents/ShineText';
import CreditIcon from '@/components/UIComponents/UserCredit/icon/credit';
import { UseTranslateType } from '@/hooks/useTranslate';
import { DateType } from '@/store/Payment';
import UserStore, { IDENTITYKEY } from '@/store/User';
import { VIPWEIGHT } from '@/store/User/const';

import { getUserTitle } from '../../utils';
import { TabContentProps } from '..';
import YesIcon from '../icon/yes';
import {
  findClosestNumber,
  formatMonthDisplayPrice,
  formatYearDisplayPrice,
  getPrevVipType,
} from '../utils';
import BuyBtn from './buyBtn';
import CreditSlider from './creditSlider';
import CurrencySelector, { useCurrencyFilteredCommodities } from './currencySelector';

const ComboItem = ({
  type,
  prevType,
  config,
  activeDateType,
  highlight,
  weight,
  limitConfig,
  loading,
  onClick,
  t,
}: {
  type: keyof LimitConfig;
  prevType: keyof LimitConfig;
  config: LimitConfig[keyof LimitConfig];
  activeDateType: DateType;
  highlight: boolean;
  limitConfig: LimitConfig;
  weight: number;
  loading: boolean;
  onClick: (price: string, currency: string, selectedCommodityCode: string) => void;
  t?: UseTranslateType;
}) => {
  const getCurrentDiscountInfo = UserStore((store) => store.getCurrentDiscountInfo);
  const [selectedCommodityCode, setSelectedCommodityCode] = useState('');

  const rightContext = useContext(RightContext);
  const { getUserLoginState } = rightContext;

  // 根据当前选择的时间周期获取商品数据数组
  const commodityDataArray: any[] =
    config?.commodityData[
      activeDateType === 'year'
        ? 'year'
        : activeDateType === 'month'
          ? 'month'
          : 'twoweek'
    ] || [];
  const filteredCommodityDataArray = useCurrencyFilteredCommodities(commodityDataArray);
  const displayCommodityDataArray =
    filteredCommodityDataArray.length > 0
      ? filteredCommodityDataArray
      : commodityDataArray;
  // 在组件初始化时设置默认的商品code
  useEffect(() => {
    // 确保组件数据存在
    if (
      !Array.isArray(displayCommodityDataArray) ||
      displayCommodityDataArray.length === 0
    ) {
      return;
    }

    const currentSelectedValid = selectedCommodityCode
      ? displayCommodityDataArray.some(
          (item) => item.commodityCode === selectedCommodityCode,
        )
      : false;
    if (currentSelectedValid) {
      return;
    }

    // 初始默认值，先设置为第一个商品的编码
    let defaultCommodityCode = displayCommodityDataArray[0].commodityCode;

    // 检查window对象是否存在（仅在客户端环境中执行）
    if (!selectedCommodityCode && typeof window !== 'undefined') {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const codeFromUrl = urlParams.get('commodityCode');

        // 如果URL中有商品编码且该编码在可选列表中存在
        if (
          codeFromUrl &&
          displayCommodityDataArray.some((item) => item.commodityCode === codeFromUrl)
        ) {
          defaultCommodityCode = codeFromUrl;
        }
      } catch (error) {
        // 捕获可能的错误，确保代码不会在SSR环境中崩溃
        console.error('Error parsing URL parameters:', error);
      }
    }

    // 设置选中的商品编码
    if (defaultCommodityCode !== selectedCommodityCode) {
      setSelectedCommodityCode(defaultCommodityCode);
    }
  }, [displayCommodityDataArray, selectedCommodityCode]);

  if (
    !Array.isArray(displayCommodityDataArray) ||
    displayCommodityDataArray.length === 0
  ) {
    return;
  }
  if (!t) {
    return;
  }
  // 确定是否有多个商品可选
  const hasMultipleOptions =
    Array.isArray(displayCommodityDataArray) && displayCommodityDataArray.length > 1;

  // 获取当前选中的商品
  const commodityData =
    displayCommodityDataArray.find(
      (item) => item.commodityCode === selectedCommodityCode,
    ) || displayCommodityDataArray[0];

  const currency = commodityData?.content?.currency === CommodityCurrency.CNY ? '¥' : '$';

  const originalPrice = Number(commodityData?.originalPrice / 100) || 0;
  let realPrice = Number(commodityData?.realPrice / 100) || 0;

  // 优惠相关
  const {
    realPrice: disCountResultData,
    originPrice,
    hasCoupon: currentCodeHasCoupon,
  } = getCurrentDiscountInfo(realPrice, commodityData.commodityCode);

  realPrice = disCountResultData || originPrice;

  // 付费会员且有优惠
  const hasCouponFlag = currentCodeHasCoupon && type !== 'freeVip';

  const coin =
    type === 'enterpriseVip'
      ? t('merror.sub.custom')
      : commodityData?.content?.point || 0;
  const dateUnit =
    type === 'freeVip'
      ? t('bc.month')
      : commodityData?.content?.durationType === CommodityDate.YEAR
        ? t('bc.year')
        : commodityData?.content?.durationType === CommodityDate.MONTH
          ? t('bc.month')
          : t('bc.biweekly');

  const Corner = (
    <div
      className="transition absolute -right-[2px] -top-[2px] px-[12px] py-[4px] text-[14px] flex justify-center items-center tracking-[0.14px] font-[500] text-white"
      style={{
        borderRadius: '0 10px 0 8px',
        background: 'linear-gradient(270deg, #EF5CFF 0%, #4D2FFF 100%)',
      }}
    >
      {t('merror.sub.purchaseBadge')}
    </div>
  );

  // 是否展示划线价
  const hasDisCount = originalPrice !== realPrice;
  const UnDisCountComp = (
    <ShineText useShine={hasCouponFlag}>
      <span
        className={`text-[48px] font-[700] leading-[56px] tracking-[-1px] text-black`}
      >
        <span className="text-[18px] leading-[32px]">{currency}</span>
        <span className="font-gabarito">
          {formatMonthDisplayPrice(
            realPrice,
            activeDateType as Extract<DateType, 'year' | 'month'>,
          )}
        </span>
      </span>
    </ShineText>
  );
  const DisCountComp = (
    <>
      <ShineText useShine={hasCouponFlag}>
        <span
          className={`text-[48px] font-[700] leading-[56px] tracking-[-1px] text-black`}
        >
          <span className="text-[18px] leading-[32px]">{currency}</span>
          <span className="font-gabarito">
            {formatMonthDisplayPrice(
              realPrice,
              activeDateType as Extract<DateType, 'year' | 'month'>,
            )}
          </span>
        </span>
      </ShineText>
      <span className="text-payment-modal-card-title-color text-line-through opacity-50">
        <span className="text-[20px]">{currency}</span>
        <span className="leading-[24px] font-[500] text-[20px]">
          {formatMonthDisplayPrice(
            originalPrice,
            activeDateType as Extract<DateType, 'year' | 'month'>,
          )}
        </span>
      </span>
    </>
  );

  const Price = (
    <div className="flex flex-col items-start pt-[12px] h-[104px]">
      {type === 'enterpriseVip' ? (
        <div className="text-[36px] font-[600] leading-[55px] trakcing-[1px]">
          {t('merror.sub.priceEnterprise')}
        </div>
      ) : (
        <div className="inline-flex items-end gap-[8px] ">
          {hasDisCount ? DisCountComp : UnDisCountComp}
          {/* {CouponCard} */}
        </div>
      )}
      <p className="text-[14px] font-[400] leading-[24px] pt-[2px] text-payment-modal-card-text-secondary">
        {type === 'freeVip'
          ? t('Welcome-to-try-it-out')
          : type === 'enterpriseVip'
            ? t('Customized-to-your-needs')
            : activeDateType === 'month'
              ? t('merror.sub.pmonth')
              : activeDateType === 'twoweek'
                ? t('bc.biweekly')
                : `${t('per-month-billed-annually')}${currency}${formatYearDisplayPrice(realPrice, activeDateType as Extract<DateType, 'year' | 'month'>)}`}
      </p>
    </div>
  );

  const Coin = (
    <div className="flex items-center gap-[6px]">
      <CreditIcon />
      {type === 'enterpriseVip' ? (
        <span className="text-[16px] font-[500] leading-[24px] inline-flex items-center text-[#4931FA]">
          {coin}
        </span>
      ) : (
        <div className="flex items-center ">
          <span className="text-[18px] font-[500] leading-[24px] pr-[6px] text-[#4931FA]">
            {coin}
          </span>
          <span className="text-[14px]">
            {t('merror.sub.cre')}/{dateUnit}
          </span>
        </div>
      )}
    </div>
  );

  // feature前缀
  const PrefixFeature =
    type === 'enterpriseVip' ? null : (
      <div className="pt-[24px]">
        {prevType ? (
          <div className="flex items-center gap-[6px]">
            <span className="text-[14px] font-[400] leading-[20px] font-sans text-payment-modal-card-text-color">
              {t(getPrevVipType(prevType))}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-[6px]">
            <span className="text-[14px] font-[400] leading-[20px] font-sans text-payment-modal-card-text-color">
              {t('PaymentModal-FreeVip-Level-Tip')}
            </span>
          </div>
        )}
      </div>
    );

  const FeatureList = () => {
    return (
      <>
        {config.featureDesc[activeDateType].map((item, index) => {
          return (
            <div key={index}>
              <div className="flex items-center gap-[6px]">
                <YesIcon />
                <span className="text-[14px] font-[400] leading-[24px] font-sans text-payment-modal-card-text-color">
                  {item.value ? t(item.label, item.value) : t(item.label)}
                </span>
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const CreditSelect =
    hasMultipleOptions && !['freeVip', 'enterpriseVip'].includes(type) ? (
      <CreditSlider
        defaultValue={commodityData?.content?.point}
        options={displayCommodityDataArray.map((item) => item.content.point)}
        onChange={(value) => {
          const selectedItem = displayCommodityDataArray.find(
            (item) => item?.content?.point === value,
          );
          if (selectedItem) {
            setSelectedCommodityCode(selectedItem.commodityCode);
          }
        }}
      />
    ) : (
      <div className="h-[54px]"></div>
    );

  return (
    <div
      className={`
        shrink-0 box-border max-w-[360px] p-[2px] flex flex-col flex-1 min-w-[284px] min-h-[600px]  rounded-[10px] relative overflow-hidden select-none bg-white
         shadow-[0px_4px_2px_0px_rgba(0,0,0,0.01),0px_2px_1px_0px_rgba(0,0,0,0.05),0px_1px_1px_0px_rgba(0,0,0,0.08),0px_0px_1px_0px_rgba(0,0,0,0.09)]
        `}
      style={{
        background: highlight ? 'linear-gradient(90deg, #4D2FFF, #EF5CFF)' : '#FFFFFF',
      }}
    >
      <div
        style={{
          height: '100%',
          padding: '0 40px',
          paddingBottom: '12px',
          borderRadius: 8,
          background: '#ffffff',
        }}
      >
        {highlight ? Corner : null}
        {/* 标题 */}
        <div className="pt-[32px] text-[16px] leading-[22px] font-[400] text-payment-modal-card-title-color">
          {t ? t(getUserTitle(type)) : ''}
        </div>
        {/* 价格和描述 */}
        <div>{Price}</div>
        <div className="flex items-center">
          {/* 选择套餐 */}
          {CreditSelect}
        </div>
        {/* 购买按钮 */}
        <div className="relative">
          <BuyBtn
            type={type}
            weight={weight}
            loading={loading}
            activeDateType={activeDateType}
            onClick={() => {
              getUserLoginState({
                onReady() {
                  // 传递当前选中商品的价格和货币类型
                  onClick(
                    formatMonthDisplayPrice(realPrice),
                    currency,
                    selectedCommodityCode,
                  );
                },
              });
            }}
            t={t}
          />
        </div>
        {PrefixFeature}
        {/* 功能点 */}
        <div className="flex flex-col gap-[10px] pt-[22px] overflow-hidden">
          <div>{Coin}</div>
          {FeatureList()}
        </div>
      </div>
    </div>
  );
};

const ComboContent = ({
  t,
  formatData,
  weightData,
  activeDateType,
  highlightBenefit,
  loadingStates,
  onClick,
}: {
  t?: UseTranslateType;
  formatData: [keyof LimitConfig, LimitConfig[keyof LimitConfig]][];
  weightData: number[];
  activeDateType: DateType;
  loadingStates: boolean[];
  highlightBenefit: IDENTITYKEY;
  onClick: (
    key: UserIdentity,
    config: LimitConfig[keyof LimitConfig],
    btnIndex: number,
    price: string,
    currency: string,
    selectedCommodityCode: string,
  ) => void;
}) => {
  const { limitConfig } = useContext(RightContext);

  // 获取当前选中周期的所有商品（用于 CurrencySelector）
  const currentCommodities = useMemo(() => {
    const allCommodities: any[] = [];
    formatData.forEach(([, config]) => {
      const commodities = config.commodityData?.[activeDateType] || [];
      allCommodities.push(...commodities);
    });
    return allCommodities;
  }, [formatData, activeDateType]);

  return (
    <div className="flex flex-1 justify-center">
      <div className="flex justify-center flex-col  px-[60px] combo-content max-w-fit">
        <CurrencySelector
          commodities={currentCommodities}
          disabled={loadingStates.some((state) => state)}
        />
        <div className="flex justify-center gap-[8px] px-[60px] combo-content ">
          {formatData.map(([key, config], btnIndex) => {
            const prevWeight = findClosestNumber(
              weightData,
              config.weight || VIPWEIGHT.freeVip,
            );
            let prevType =
              (Object.entries(limitConfig).find(
                ([, value]) => value.weight === prevWeight,
              )?.[0] as keyof LimitConfig) || '';
            return (
              <ComboItem
                t={t}
                type={key as keyof LimitConfig}
                prevType={prevType || ''}
                weight={config.weight}
                activeDateType={
                  activeDateType as Extract<
                    TabContentProps['activeKey'],
                    'year' | 'month'
                  >
                }
                highlight={Boolean(highlightBenefit === key && activeDateType === 'year')}
                limitConfig={limitConfig}
                config={config}
                loading={loadingStates[btnIndex]}
                onClick={(price, currency, selectedCommodityCode) => {
                  onClick(key, config, btnIndex, price, currency, selectedCommodityCode);
                }}
                key={btnIndex}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
export default ComboContent;
